// Типы для админских операций
import type {
    PatronageUpdatePayload,
    PatronListParams,
} from "../types/patronage";

export interface TopUpUserRequest {
    user_ref_link: string;
    sum: number;
    additional_desc?: string;
}

export interface TopUpUserTokenRequest {
    user_id: number;
    sum: number;
}

export interface TopUpCharityRequest {
    users_donate_id: number;
    sum: number;
}

export interface GiftSharesRequest {
    user_id: number;
    num: number;
}

export interface ManualCreditSharesPayRequest {
    user_id: number;
    amount: number;
}

export interface UpdateSharesSettingsRequest {
    available_for_sale: number;
    shares_credit: number;
    shares_sell: number;
}

export interface ChangeUserPasswordRequest {
    user_id: number;
    password: string;
}

export interface AuthorizeInvestmentUserRequest {
    user_id: number;
    url: string;
}

export interface RestoreMarathonUserAndTicketsRequest {
    user_id: number;
    marathon_id: number;
}

export interface SharesStatistics {
    total_shares: number;
    total_users: number;
    daily_percent: number;
    purchased: { total: number; users: number };
    gift: { total: number; users: number };
    from_users: { total: number; users: number };
    credit: { total: number; users: number };
}

export interface SharesSettings {
    available_for_sale: number;
    shares_credit: number;
    shares_sell: number;
}

export interface CreditShareUser {
    id: number;
    name: string;
    surname: string;
    email: string;
    ref_link: string;
}

export interface CreditShare {
    user_id: number;
    quantity: number;
    money: number;
    day: number;
    paid: number;
    payed: number;
    start: number;
    comment: string;
    immunity: boolean;
    user: CreditShareUser | null;
    next_payment_date: string | null;
    is_payment_due: boolean;
}

export interface CreateCreditShareRequest {
    user_id: number;
    quantity: number;
    money: number;
    day: number;
    paid?: number;
    payed?: number;
    start?: number;
    comment?: string;
    immunity?: boolean;
}

export interface UpdateCreditShareRequest {
    quantity?: number;
    money?: number;
    day?: number;
    paid?: number;
    payed?: number;
    start?: number;
    comment?: string;
    immunity?: boolean;
}

export interface InvestmentListItem {
    user_id: number;
    user_name: string;
    user_surname: string;
    strategy: number;
    total_invested: number;
    money_active: number;
    money_pending: number;
    earned: number;
    withdraw: number;
    total_earned: number;
    available_for_withdraw: number;
}

export interface GetInvestmentsParams {
    strategy?: number;
    user_id?: number;
    sort_by?: 'total' | 'available';
    sort_order?: 'asc' | 'desc';
}

export interface GetInvestmentsResponse {
    success: boolean;
    data: InvestmentListItem[];
    has_pending?: boolean;
    pending_count?: number;
    global_has_pending?: boolean;
    global_pending_count?: number;
    pending_by_strategy?: Record<number, { count: number; sum: number }>;
    meta?: {
        strategy: number;
        total_count: number;
        sort_by: string;
        sort_order: string;
    };
}

export interface InvestmentStrategyData {
    money_active: number;
    money_pending: number;
    money_withdraw_pending: number;
    money_transfer_pending: string;
    total: number;
    withdraw: number;
    earned: number;
}

export interface GetInvestmentStrategyParams {
    user_id: number;
    strategy: number;
}

export interface GetInvestmentStrategyResponse {
    success: boolean;
    data: InvestmentStrategyData;
}

export interface UpdateInvestmentStrategyParams {
    user_id: number;
    strategy: number;
    money_active?: number;
    money_pending?: number;
    money_withdraw_pending?: number;
    money_transfer_pending?: string;
    total?: number;
    withdraw?: number;
    earned?: number;
}

export interface UpdateInvestmentStrategyResponse {
    success: boolean;
    message: string;
}

export interface InvestmentDailyStat {
    id: number;
    strategy: number;
    strategy_name: string;
    percent: number;
    date: string;
    datetime: string;
    time: number;
}

export interface InvestmentWeeklyStat {
    id: number;
    strategy: number;
    strategy_name: string;
    sum: number;
    date: string;
}

export interface InvestmentStatsSummary {
    total_sum: number;
    count: number;
}

export interface AggregatedStrategyStat {
    strategy_id: number;
    strategy_name: string;
    current_percent: number;
    weeks_active: number;
    average_percent: number;
}

export interface GetInvestmentStatsParams {
    strategy?: number;
    date_from?: string;
    date_to?: string;
    limit?: number;
}

export interface GetInvestmentDailyStatsResponse {
    success: boolean;
    data: {
        stats: InvestmentDailyStat[];
        summary: InvestmentStatsSummary;
    };
}

export interface GetInvestmentWeeklyStatsResponse {
    success: boolean;
    data: {
        stats: InvestmentWeeklyStat[];
        summary: InvestmentStatsSummary;
    };
}

export interface GetAggregatedStrategyStatsResponse {
    success: boolean;
    data: AggregatedStrategyStat[];
}

export interface UserSharesSummary {
    user_id: number;
    quantity: number;
    first_time: string | null;
    last_time: string | null;
    total_value: number;
    user: {
        id: number;
        name: string;
        surname: string;
        email: string;
    } | null;
}

export interface UserShare {
    alny_shares_users_id: number;
    alny_shares_users_time: number;
    alny_shares_users_type: number;
    alny_shares_users_user_id: number;
}

export interface GetPurchasedUserSharesByUserIdRequest {
    userId: number;
}

export interface GetGiftedUserSharesByUserIdRequest {
    userId: number;
}

export interface DeletePurchasedSharesRequest {
    shareId: number;
}

export interface DeleteGiftedSharesRequest {
    shareId: number;
}

export const adminEndpoints = {
    // Balance Management
    topUpUser: {
        query: (data: TopUpUserRequest) => ({
            url: "/admin/balance/topup-user",
            method: "POST",
            body: data,
        }),
    },

    topUpUserToken: {
        query: (data: TopUpUserTokenRequest) => ({
            url: "/admin/balance/topup-user-token",
            method: "POST",
            body: data,
        }),
    },

    topUpCharity: {
        query: (data: TopUpCharityRequest) => ({
            url: "/admin/balance/topup-charity",
            method: "POST",
            body: data,
        }),
    },

    giftShares: {
        query: (data: GiftSharesRequest) => ({
            url: "/admin/balance/gift-shares",
            method: "POST",
            body: data,
        }),
    },

    manualPayCreditShares: {
        query: (data: ManualCreditSharesPayRequest) => ({
            url: "/admin/balance/credit-shares/manual-pay",
            method: "POST",
            body: data,
        }),
    },

    // Shares Management
    getSharesStatistics: {
        query: () => "/admin/balance/shares-statistics",
    },

    getSharesSettings: {
        query: () => "/admin/balance/shares-settings",
    },

    updateSharesSettings: {
        query: (data: UpdateSharesSettingsRequest) => ({
            url: "/admin/balance/shares-settings",
            method: "POST",
            body: data,
        }),
    },

    // User Shares (purchased / gifted)
    getPurchasedUserShares: {
        query: () => "/admin/user-shares/purchased",
    },

    getPurchasedUserSharesByUserId: {
        query: (data: GetPurchasedUserSharesByUserIdRequest) => ({
            url: `/admin/user-shares/purchased/${data.userId}`,
            method: "GET",
        }),
    },

    deletePurchasedShares: {
        query: (data: DeletePurchasedSharesRequest) => ({
            url: `/admin/user-shares/purchased/${data.shareId}`,
            method: "DELETE",
        }),
    },

    getGiftedUserShares: {
        query: () => "/admin/user-shares/gifted",
    },

    getGiftedUserSharesByUserId: {
        query: (data: GetGiftedUserSharesByUserIdRequest) => ({
            url: `/admin/user-shares/gifted/${data.userId}`,
            method: "GET",
        }),
    },

    deleteGiftedShares: {
        query: (data: DeleteGiftedSharesRequest) => ({
            url: `/admin/user-shares/gifted/${data.shareId}`,
            method: "DELETE",
        }),
    },

    transferShares: {
        query: (data: {
            from_user_id: number;
            quantity: number;
            to_user_id: number;
            shares_type: 0 | 1; // 0 - подарочные, 1 - купленные
        }) => ({
            url: "/admin/user-shares/transfer",
            method: "POST",
            body: data,
        }),
    },
    transferCreditShares: {
        query: (data: {
            from_user_id: number;
            quantity: number;
            to_user_id: number;
        }) => ({
            url: "/admin/credit-shares/transfer",
            method: "POST",
            body: data,
        }),
    },

    addDailyPercent: {
        query: (data: { daily_percent: number; type: number }) => ({
            url: "/admin/investments/add-daily-percent",
            method: "POST",
            body: data,
        }),
    },

    // User Management
    changeUserPassword: {
        query: (data: ChangeUserPasswordRequest) => ({
            url: "/admin/special/change-password",
            method: "POST",
            body: data,
        }),
    },

    authorizeInvestmentUser: {
        query: (data: AuthorizeInvestmentUserRequest) => ({
            url: "/admin/users/authorize-investment",
            method: "POST",
            body: data,
        }),
    },

    // Withdrawals
    confirmWithdrawAll: {
        query: () => ({
            url: "/admin/investments/confirm-withdraw-all",
            method: "POST",
        }),
    },

    confirmTransferAll: {
        query: () => ({
            url: "/admin/investments/confirm-transfer-all",
            method: "POST",
        }),
    },

    getInvestmentWithdrawRequests: {
        query: () => "/admin/investments/withdraw-pending",
    },

    getInvestmentTransferRequests: {
        query: () => "/admin/investments/transfer-pending",
    },

    getInvestments: {
        query: (params?: GetInvestmentsParams) => {
            const queryParams = new URLSearchParams();
            if (params?.strategy !== undefined) {
                queryParams.append('strategy', params.strategy.toString());
            }
            if (params?.user_id !== undefined) {
                queryParams.append('user_id', params.user_id.toString());
            }
            if (params?.sort_by) {
                queryParams.append('sort_by', params.sort_by);
            }
            if (params?.sort_order) {
                queryParams.append('sort_order', params.sort_order);
            }
            const queryString = queryParams.toString();
            return `/admin/investments/list${queryString ? `?${queryString}` : ''}`;
        },
    },

    directWithdraw: {
        query: (data: { user_id: number; strategy: number; amount: number }) => ({
            url: "/admin/investments/direct-withdraw",
            method: "POST",
            body: data,
        }),
    },

    confirmInvestmentWithdraw: {
        query: (data: { user_id: number; strategy: number }) => ({
            url: "/admin/investments/confirm-withdraw",
            method: "POST",
            body: data,
        }),
    },

    cancelInvestmentWithdraw: {
        query: (data: {
            user_id: number;
            strategy: number;
            comment?: string;
        }) => ({
            url: "/admin/investments/cancel-withdraw",
            method: "POST",
            body: data,
        }),
    },

    confirmInvestmentTransfer: {
        query: (data: {
            user_id: number;
            from_strategy: number;
            to_strategy: number;
            amount: number;
        }) => ({
            url: "/admin/investments/confirm-transfer",
            method: "POST",
            body: data,
        }),
    },

    cancelInvestmentTransfer: {
        query: (data: {
            user_id: number;
            from_strategy: number;
            to_strategy: number;
            amount: number;
        }) => ({
            url: "/admin/investments/cancel-transfer",
            method: "POST",
            body: data,
        }),
    },

    getInvestmentStrategy: {
        query: (params: GetInvestmentStrategyParams) => {
            const queryParams = new URLSearchParams();
            queryParams.append('user_id', params.user_id.toString());
            queryParams.append('strategy', params.strategy.toString());
            return `/admin/investments/strategy?${queryParams.toString()}`;
        },
    },

    updateInvestmentStrategy: {
        query: (data: UpdateInvestmentStrategyParams) => ({
            url: "/admin/investments/strategy",
            method: "PUT",
            body: data,
        }),
    },

    goldControl: {
        query: (data: { gold_control: string }) => ({
            url: "/admin/special/gold-control",
            method: "POST",
            body: data,
        }),
    },

    getPendingJivo: {
        query: () => "/admin/jivo/pending",
    },

    confirmJivo: {
        query: (data: { jivo_id: number; code: string }) => ({
            url: "/admin/jivo/confirm",
            method: "POST",
            body: data,
        }),
    },

    payMarathon: {
        query: () => ({
            url: "/admin/special/pay-marathon",
            method: "POST",
        }),
    },

    restoreMarathonUserAndTickets: {
        query: (data: RestoreMarathonUserAndTicketsRequest) => ({
            url: "/admin/special/restore-marathon-user-and-tickets",
            method: "POST",
            body: data,
        }),
    },

    // Credit Shares Management
    getCreditShares: {
        query: () => "/admin/credit-shares",
    },

    getCreditShare: {
        query: (userId: number) => `/admin/credit-shares/${userId}`,
    },

    createCreditShare: {
        query: (data: CreateCreditShareRequest) => ({
            url: "/admin/credit-shares",
            method: "POST",
            body: data,
        }),
    },

    updateCreditShare: {
        query: (data: { userId: number; data: UpdateCreditShareRequest }) => ({
            url: `/admin/credit-shares/${data.userId}`,
            method: "PUT",
            body: data.data,
        }),
    },

    deleteCreditShare: {
        query: (userId: number) => ({
            url: `/admin/credit-shares/${userId}`,
            method: "DELETE",
        }),
    },

    // Patronage (Patrons)
    getPatrons: {
        query: (params: PatronListParams) => ({
            url: "/admin/patronage/patrons",
            params,
        }),
    },

    getPatronageDetails: {
        query: (userId: number) => `/admin/patronage/patrons/${userId}`,
    },

    updatePatronage: {
        query: (data: { userId: number; data: PatronageUpdatePayload }) => ({
            url: `/admin/patronage/patrons/${data.userId}`,
            method: "PATCH",
            body: data.data,
        }),
    },

    // Investment Statistics
    getDailyPercentStats: {
        query: (params?: GetInvestmentStatsParams) => {
            const queryParams = new URLSearchParams();
            if (params?.strategy !== undefined) {
                queryParams.append('strategy', params.strategy.toString());
            }
            if (params?.date_from) {
                queryParams.append('date_from', params.date_from);
            }
            if (params?.date_to) {
                queryParams.append('date_to', params.date_to);
            }
            if (params?.limit !== undefined) {
                queryParams.append('limit', params.limit.toString());
            }
            const queryString = queryParams.toString();
            return `/admin/investments/daily-percent-stats${queryString ? `?${queryString}` : ''}`;
        },
    },

    getWeeklyPercentStats: {
        query: (params?: GetInvestmentStatsParams) => {
            const queryParams = new URLSearchParams();
            if (params?.strategy !== undefined) {
                queryParams.append('strategy', params.strategy.toString());
            }
            if (params?.date_from) {
                queryParams.append('date_from', params.date_from);
            }
            if (params?.date_to) {
                queryParams.append('date_to', params.date_to);
            }
            if (params?.limit !== undefined) {
                queryParams.append('limit', params.limit.toString());
            }
            const queryString = queryParams.toString();
            return `/admin/investments/weekly-percent-stats${queryString ? `?${queryString}` : ''}`;
        },
    },

    getAggregatedStrategyStats: {
        query: () => "/admin/investments/aggregated-stats",
    },

    // Strategy Management
    getStrategies: {
        query: () => "/admin/investments/strategies",
    },

    updateStrategy: {
        query: (data: { id: number; payload: UpdateInvestmentStrategyPayload }) => ({
            url: `/admin/investments/strategies/${data.id}`,
            method: "PUT",
            body: data.payload,
        }),
    },
};

export interface InvestmentStrategy {
    id: number;
    color: string;
    image: string;
    is_active: boolean;
    sort_order: number;
    name: string;
    description: string;
    translations: {
        id: number;
        investment_strategy_id: number;
        locale: string;
        name: string;
        description: string;
    }[];
}

export interface UpdateInvestmentStrategyPayload {
    color?: string;
    image?: string;
    is_active?: boolean;
    sort_order?: number;
    translations: Record<string, {
        name: string;
        description?: string;
    }>;
}

export const adminTags = [
    "Admin",
    "Balance",
    "Shares",
    "Users",
    "Withdrawals",
    "Patronage",
] as const;
